import { useCallback, useEffect, useState } from "react";
import { errorMessage } from "../../core/errors";
import type { ProjectSummary } from "../../project/storage";
import { MAX_PROJECT_BYTES } from "../../project/serialize";
import { Panel } from "../components/controls";
import { SongDetails } from "../components/SongDetails";
import { useDirty, useProject, useServices } from "../context";
import { downloadText, readTextFile } from "../download";

export function Projects() {
  const { controller, library, setlists } = useServices();
  const name = useProject((p) => p.name);
  const id = useProject((p) => p.id);
  const dirty = useDirty();
  const [list, setList] = useState<ProjectSummary[]>([]);
  const [copyName, setCopyName] = useState("");
  const refresh = useCallback(async () => setList(await library.listProjects()), [library]);
  useEffect(() => {
    let alive = true;
    void library.listProjects().then((l) => { if (alive) setList(l); });
    return () => { alive = false; };
  }, [library]);

  /** Never discard unsaved changes without asking. */
  const confirmDiscard = () => !dirty || window.confirm("You have unsaved changes. Discard them?");
  const rename = (v: string) => controller.updateProject((p) => ({ ...p, name: v.slice(0, 120) || p.name }));

  return (
    <div className="workspace-grid projects">
      <Panel title="Current project">
        <label className="inline-field">
          <span>Name</span>
          <input defaultValue={name} key={id} maxLength={120} onBlur={(e) => rename(e.currentTarget.value.trim())} />
        </label>
        <p className="hint">{dirty ? "Unsaved changes." : "All changes saved."} Projects are stored in this browser (IndexedDB).</p>
        <div className="controls-row">
          <button type="button" className="primary" disabled={!dirty} onClick={async () => { await library.save(); await refresh(); }}>Save</button>
          <input aria-label="Name for copy" placeholder="Copy name" value={copyName} onChange={(e) => setCopyName(e.currentTarget.value)} />
          <button type="button" onClick={async () => { await library.saveAsCopy(copyName || `${name} copy`); setCopyName(""); await refresh(); }}>Save as copy</button>
          <button type="button" onClick={() => { if (confirmDiscard()) library.newProject(); }}>New project</button>
        </div>
        <div className="controls-row">
          <button type="button" onClick={() => { const f = library.exportCurrent(); downloadText(f.filename, f.json); }}>Export file</button>
          <label className="file-button">
            Import file…
            <input type="file" accept=".json,application/json" className="sr-only" onChange={async (e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = "";
              if (!file || !confirmDiscard()) return;
              try { await library.importJson(await readTextFile(file, MAX_PROJECT_BYTES)); } catch (err) { controller.notify("error", errorMessage(err)); }
            }} />
          </label>
        </div>
        <p className="hint">Exported files contain the arrangement and settings but not imported audio samples; on another device, load the samples again.</p>
      </Panel>
      <SongDetails />
      <Panel title="Saved projects" actions={<button type="button" onClick={() => void refresh()}>Refresh</button>}>
        {list.length === 0 ? <p className="hint">No saved projects yet. Press Save to keep this one.</p> : (
          <ul className="project-list">
            {list.map((p) => (
              <li key={p.id} className={p.id === id ? "current" : ""}>
                <span className="project-title">{p.name}</span>
                <span className="hint">{new Date(p.updatedAt).toLocaleString()} · rev {p.revision}</span>
                <span className="row-actions">
                  <button type="button" disabled={p.id === id} onClick={async () => { if (confirmDiscard()) await library.open(p.id); }}>Open</button>
                  <button type="button" onClick={async () => {
                    if (window.confirm(`Delete "${p.name}" permanently from this browser?`)) { await library.remove(p.id); await refresh(); await setlists.refreshSongs(); }
                  }}>Delete</button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
